/**
 * Sign-in page — email/password, Google, and Microsoft.
 *
 * Renders WITHOUT a session (before the SPA's session gate). The form POSTs
 * directly to Flask's /auth/login, which sets the auth cookie and redirects
 * back into the SPA. Google uses GIS (accounts.google.com/gsi/client) and
 * POSTs the credential to /api/v1/auth/google. Microsoft redirects to the
 * Flask OAuth flow (/auth/microsoft).
 *
 * The Google client ID comes from /api/v1/auth/config — a public endpoint
 * that exposes the OAuth client IDs the browser needs.
 */
import { useEffect, useRef, useState } from 'react'
import { trackEvent } from '../../lib/analytics'
import { API_BASE } from '../../lib/api'
import './auth.css'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void
          renderButton: (el: HTMLElement, config: Record<string, unknown>) => void
        }
      }
    }
  }
}

export default function Login() {
  const nextUrl = new URLSearchParams(window.location.search).get('next') ?? ''
  const ERROR_MESSAGES: Record<string, string> = {
    'bad-credentials': 'Invalid email or password.',
    'send-code-failed': 'Could not send the verification code. Try again.',
  }
  const errorCode = new URLSearchParams(window.location.search).get('error')
  const [error, setError] = useState<string | null>(
    errorCode ? (ERROR_MESSAGES[errorCode] ?? 'Sign-in failed.') : null
  )

  const [googleClientId, setGoogleClientId] = useState<string | null>(null)
  const [msEnabled, setMsEnabled] = useState(false)
  const [googleReady, setGoogleReady] = useState(false)
  const googleRef = useRef<HTMLDivElement>(null)

  // login.html's funnel, ported: page view on load, form-started on the
  // first field focus (whichever field), submitted with time-to-complete.
  const loginStart = useRef<number | null>(null)

  useEffect(() => {
    trackEvent('Login Page View', {
      page: 'login',
      referrer: document.referrer,
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
    })
  }, [])

  const onFieldFocus = (field: 'email' | 'password') => {
    if (loginStart.current !== null) return
    loginStart.current = Date.now()
    trackEvent('Login Form Started', { field })
  }

  // Fetch OAuth config (public endpoint — client IDs are not secrets)
  useEffect(() => {
    fetch(`${API_BASE}/api/v1/auth/config`)
      .then((r) => r.json())
      .then((d: { google_client_id: string | null; microsoft_enabled: boolean }) => {
        if (d.google_client_id) setGoogleClientId(d.google_client_id)
        setMsEnabled(d.microsoft_enabled)
      })
      .catch(() => { /* config unavailable — OAuth buttons stay hidden */ })
  }, [])

  // Load Google Identity Services script once we have a client ID
  useEffect(() => {
    if (!googleClientId) return
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => setGoogleReady(true)
    document.head.appendChild(script)
    return () => { document.head.removeChild(script) }
  }, [googleClientId])

  // Initialize GIS once the script loads
  useEffect(() => {
    if (!googleReady || !googleRef.current || !googleClientId) return
    const g = window.google
    if (!g?.accounts?.id) return
    try {
      g.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: { credential: string }) => {
          try {
            const qs = nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ''
            const res = await fetch(`${API_BASE}/api/v1/auth/google${qs}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ credential: response.credential }),
              redirect: 'follow',
            })
            if (res.redirected) { window.location.href = res.url; return }
            if (res.ok) { window.location.href = '/'; return }
            const body = await res.json().catch(() => ({}))
            setError((body as { error?: string }).error ?? `Sign-in failed (${res.status})`)
          } catch {
            setError('Could not reach the server. Check your connection.')
          }
        },
      })
      g.accounts.id.renderButton(googleRef.current, {
        theme: 'outline', size: 'large', text: 'signin_with', width: 340,
      })
    } catch {
      // GIS refused to render (blocked by extension, hidden container, etc.)
    }
  }, [googleReady, googleClientId, nextUrl])

  const msUrl = `${API_BASE}/auth/microsoft${nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ''}`
  const formAction = `${API_BASE}/auth/login${nextUrl ? `?next=${encodeURIComponent(nextUrl)}` : ''}`

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">Priori Sports</div>
        <h1 className="auth-title">Sign in</h1>

        {error && <div className="auth-flash auth-flash--error">{error}</div>}

        <div className="auth-oauth">
          {/* Google Sign-In — rendered by GIS into this container */}
          {googleClientId && <div id="google-signin" ref={googleRef} />}

          {/* Microsoft */}
          {msEnabled && (
            <a href={msUrl} className="auth-btn auth-btn--ms">
            <svg width="16" height="16" viewBox="0 0 21 21" fill="none" aria-hidden>
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </a>
          )}
        </div>

        <div className="auth-divider">or sign in with email</div>

        <form
          action={formAction}
          method="POST"
          onSubmit={(e) => {
            const email = new FormData(e.currentTarget).get('email')
            trackEvent('Login Form Submitted', {
              // login.html sent the typed email too — the funnel joins
              // attempts to accounts before authentication exists.
              email: typeof email === 'string' ? email : null,
              time_to_complete_seconds: loginStart.current
                ? Math.round((Date.now() - loginStart.current) / 1000)
                : null,
            })
          }}
        >
          <label className="auth-field">
            <span className="auth-label">Email</span>
            <input className="auth-input" name="email" type="email"
                   placeholder="you@club.com" required autoComplete="email"
                   onFocus={() => onFieldFocus('email')} />
          </label>
          <label className="auth-field">
            <span className="auth-label">Password</span>
            <input className="auth-input" name="password" type="password"
                   required autoComplete="current-password"
                   onFocus={() => onFieldFocus('password')} />
          </label>
          <button className="auth-btn auth-btn--primary" type="submit">
            Sign in
          </button>
        </form>

        <div className="auth-links">
          <a href="/forgot-password">Forgot password?</a>
          {' · '}
          <a href="/request-access">Request access</a>
        </div>

        <div className="auth-legal">
          <a href="/terms" target="_blank" rel="noopener">Terms of Service</a>
          {' · '}
          <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>
        </div>
      </div>
    </div>
  )
}
